import React from 'react';
import { Button, Space, Card, Tabs,
    Form, Input, InputNumber, Select, Alert, message } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';

import { SearchSettings, Settings } from '@urbaneez.art/shared';

interface SearchSettingProps {
    isLoading: boolean,
    settings: Settings | undefined,
    error?: any,
    updateSearchSettings: (values: SearchSettings) => void,
}

export const SearchSettingsCard: React.FC<SearchSettingProps> = ({
    isLoading,
    settings,
    error,
    updateSearchSettings
}) => {
    
    const [form] = Form.useForm<SearchSettings>();

    const handleFinish = async (values: SearchSettings) => {
        try {
            updateSearchSettings(values);
            message.success('Search settings updated successfully!');
        } catch (err) {
            message.error('Failed to update search settings.');
        }
    };

    // Helper function to render the dynamic list for a specific language
    const renderSuggestionList = (langKey: 'en' | 'fr') => (
        <Form.List name={['searchSuggestions', langKey]}>
            {(fields, { add, remove }) => (
                <>
                    {fields.map(({ key, name, ...restField }) => (
                        <Space
                            key={key}
                            style={{ display: 'flex', marginBottom: 8 }}
                            align="baseline"
                        >
                            <Form.Item
                                {...restField}
                                name={[name, 'text']}
                                rules={[{ required: true, message: 'Text is required' }]}
                            >
                                <Input placeholder="Suggestion Text (e.g., Apple)" />
                            </Form.Item>

                            <Form.Item
                                {...restField}
                                name={[name, 'url']}
                                rules={[{ required: true, message: 'URL is required' }]}
                            >
                                <Input placeholder="URL (e.g., /search/apple)" style={{ width: 300 }} />
                            </Form.Item>

                            <MinusCircleOutlined
                                style={{ color: 'red' }}
                                onClick={() => remove(name)}
                            />
                        </Space>
                    ))}
                    <Form.Item>
                        <Button
                            type="dashed"
                            onClick={() => add()}
                            block
                            icon={<PlusOutlined />}
                            style={{ maxWidth: 500 }}
                        >
                            Add Suggestion
                        </Button>
                    </Form.Item>
                </>
            )}
        </Form.List>
    );

    // Safeguard the initial values payload using your interfaces
    const initialValues: Partial<SearchSettings> = {
        searchMethod: settings?.searchSettings?.searchMethod || 'regex',
        searchLimit: settings?.searchSettings?.searchLimit,
        searchSuggestions: settings?.searchSettings?.searchSuggestions || { en: [], fr: [] },
    };

    return (
      <Card title="Search Settings" loading={isLoading}>

        {error && (
          <Alert 
              message="Error loading settings" 
              description={error.message || 'An unexpected error occurred.'} 
              type="error" 
              showIcon 
              style={{ marginBottom: 16 }}
          />
        )}

        {!isLoading && settings && (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            // Default values in case the API returns null/empty initially
            initialValues={initialValues}
        >
            {/* Search Method Field */}
            <Form.Item
                label="Search Method"
                name="searchMethod"
                rules={[{ required: true, message: 'Please select a search method!' }]}
            >
                <Select style={{ width: 300 }}>
                    <Select.Option value="regex">Regex</Select.Option>
                    <Select.Option value="search" disabled>
                        Search (Not yet implemented)
                    </Select.Option>
                </Select>
            </Form.Item>

            <br/>
            <Form.Item  
                label="Search result limit" 
                name="searchLimit"
                rules={[{ required: true, message: 'Please enter the maximum number of search results!' }]}>
                <InputNumber />
            </Form.Item>

            {/* Dynamic Suggestions Tabs */}
            <br/>
            <Form.Item label="Search Suggestions">
                <Tabs
                    type="card"
                    items={[
                        {
                            key: 'en',
                            label: 'English',
                            children: renderSuggestionList('en'),
                        },
                        {
                            key: 'fr',
                            label: 'French',
                            children: renderSuggestionList('fr'),
                        },
                    ]}
                />
            </Form.Item>

            <br/>
            <Form.Item label={null}>
                <Button type="primary" htmlType="submit" disabled={isLoading || !!error}>
                    Save
                </Button>
            </Form.Item>
        </Form>
        )}
      </Card>
    );
};